// src/components/CartModal.jsx
import {track,confirmPurchase} from '../analytics/service';
import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
  useLayoutEffect,
} from "react";
import { Shirt, Plus, ChevronDown, CalendarDays, Truck, MapPin, CreditCard, Wallet, Trash2, LoaderCircle } from "lucide-react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import {
  listAddressOptions,
  getFornitoreCompleto,
  getHubAddressSchedule,
} from "../Admin/adminApi";
import "./CartModal.css";
import {
  toValue,
  fromValue,
  isDateClosedForType,
  parseSlotStart,
  toDateTime,
  hubSlotsToFasce,
  slotsForDate,
  slotsForDateAfter,
  availableDates,
  getBookingMaxDate,
} from "./cartSlots";
import toast from "react-hot-toast";
import { openExternalUrl, isNativeApp } from "../utils/nativeNavigation";
import {
  hapticLight,
  hapticMedium,
  hapticSuccess,
  hapticError,
  hapticWarning,
} from "../utils/haptics";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

/* ---------- helpers ---------- */
const currency = (cents) =>
  (Number(cents || 0) / 100).toLocaleString("it-IT", {
    style: "currency",
    currency: "EUR",
  });

const pad2 = (n) => String(n).padStart(2, "0");
const fmtDateLabel = (d) =>
  `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${String(
    d.getFullYear(),
  ).slice(2)}`;
const computeLeadMinutes = (items = []) => {
  let maxMin = 0;
  for (const it of items) {
    const m = Number.isFinite(Number(it.lead_minutes))
      ? Number(it.lead_minutes)
      : 0;
    const h = Number.isFinite(Number(it.lead_hours))
      ? Number(it.lead_hours)
      : 0;
    const total = m + h * 60;
    if (total > maxMin) maxMin = total;
  }
  return maxMin;
};

/* ---------- normalizzazione & filtri correlati ---------- */
function normItem(raw) {
  return {
    id:
      raw?.id ??
      raw?.item_id ??
      raw?.cross_sell_item_id ??
      raw?.id_servizio ??
      raw?.service_id ??
      `tmp-${Math.random().toString(36).slice(2)}`,
    serviceId: raw?.id_servizio ?? raw?.service_id ?? null,
    crossSellItemId: raw?.cross_sell_item_id ?? null,
    name:
      raw?.name ??
      raw?.titolo ??
      raw?.title ??
      raw?.custom_title ??
      raw?.descrizione ??
      raw?.description ??
      "",
    price: Number(
      raw?.price ??
        raw?.prezzo ??
        raw?.custom_price ??
        raw?.prezzo_unit ??
        raw?.prezzo_unitario ??
        0,
    ),
    img: raw?.immagine ?? raw?.image ?? raw?.custom_image ?? null,
    badge: raw?.badge ?? raw?.custom_badge ?? null,
    sub: raw?.note ?? raw?.custom_note ?? raw?.sub ?? raw?.subtitle ?? null,
  };
}

const isValidItem = (it) =>
  Boolean(it && typeof it.name === "string" && it.name.trim().length);

function uniqBy(arr, keyFn) {
  const seen = new Set();
  return arr.filter((x) => {
    const k = keyFn(x);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

function filterOutCartDuplicates(list, cartItems) {
  const cartServiceIds = new Set(
    cartItems.map((it) => it.id_servizio).filter(Boolean),
  );
  const cartNames = new Set(
    cartItems
      .map((it) =>
        (it.descrizione_capo || it.descrizione || "").trim().toLowerCase(),
      )
      .filter(Boolean),
  );

  const cleaned = list
    .map(normItem)
    .filter(isValidItem)
    .filter((s) => {
      const sid = s.serviceId || null;
      const nm = (s.name || "").trim().toLowerCase();
      if (sid && cartServiceIds.has(sid)) return false;
      if (nm && cartNames.has(nm)) return false;
      return true;
    });

  return uniqBy(
    cleaned,
    (x) => `${x.serviceId || ""}|${(x.name || "").toLowerCase()}`,
  );
}

function shuffleArray(arr = []) {
  const copy = arr.slice();

  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }

  return copy;
}

const MAX_SUGGESTIONS_ITEMS = 4;

/* ---------- Promo UI ---------- */
function PromoControls({ promo, discountCents, applyPromo, removePromo }) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const onApply = async () => {
    const clean = code.trim();
    if (!clean) return;
    setLoading(true);
    setErr("");
    try {
      await applyPromo(clean);
      setCode("");
    } catch (e) {
      setErr(e?.message || "Codice non valido");
    } finally {
      setLoading(false);
    }
  };

  if (promo?.code) {
    return (
      <div className="promo-applied">
        <span className="promo-chip">
          Codice: <strong>{promo.code}</strong>
        </span>
        {Number(discountCents) > 0 && (
          <span className="promo-discount">− {currency(discountCents)}</span>
        )}
        <button className="promo-remove" onClick={removePromo}>
          Rimuovi
        </button>
      </div>
    );
  }

  return (
    <div className="promo-wrap">
      <input
        className="promo-input"
        placeholder="Inserisci codice"
        value={code}
        onChange={(e) => setCode(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && onApply()}
        disabled={loading}
      />
      <button
        className="promo-btn"
        onClick={onApply}
        disabled={loading || !code.trim()}
      >
        {loading ? "…" : "Applica"}
      </button>
      {err && <div className="promo-error" role="alert">{err}</div>}
    </div>
  );
}

/* ---------- stepper UI ---------- */
function Stepper({ value, onChange }) {
  const v = Math.max(0, Number(value || 0));
  return (
    <div className="stepper" role="group" aria-label="Selettore quantità">
      <button
        type="button"
        aria-label="Diminuisci"
        onClick={() => onChange(Math.max(0, v - 1))}
      >
        −
      </button>
      <div className="value" aria-live="polite" aria-atomic="true">
        {v}
      </div>
      <button
        type="button"
        aria-label="Aumenta"
        onClick={() => onChange(v + 1)}
      >
        +
      </button>
    </div>
  );
}

function LineItem({ item, imageUrl, onQty, onRemove }) {
  const qty = Math.max(0, Number(item.qty ?? item.quantita ?? 0));
  const unitCents = Math.round(Number(item.prezzo) * 100);
  const lineCents = unitCents * qty;
  const title = item.descrizione_capo || item.descrizione || "Servizio";
  const thumbnail = imageUrl || item.immagine || item.image || item.img || item.custom_image;

  return (
    <div className="line">
      <span className="cm-item-thumb" aria-hidden="true">
        <Shirt size={24} />
        {thumbnail && <img
          key={thumbnail}
          src={thumbnail} alt="" loading="lazy"
          onError={event => { event.currentTarget.style.visibility = "hidden"; }} />}
      </span>
      <div className="cm-item-copy"><div className="item-name" title={title}>{title}</div>
        <div className="cm-unit-price">{currency(unitCents)} <span aria-hidden>cad.</span></div>
      </div>
      <Stepper
        value={qty}
        onChange={async (q) => {
          if (q <= 0) {
            await hapticWarning();
            onRemove?.(item.id);
          } else {
            await hapticLight();
            onQty?.(item.id, q);
          }
        }}
      />
      <div
        className="price-right"
        style={{ display: "grid", justifyItems: "end", lineHeight: 1 }}
      >
        <div style={{ fontWeight: 700 }}>{currency(lineCents)}</div>

      </div>
    </div>
  );
}

function ChipCard({ title, price, onAdd }) {
  if (!title || !String(title).trim()) return null;
  const fmt = (eur) =>
    (Math.round(Number(eur || 0) * 100) / 100).toLocaleString("it-IT", {
      style: "currency",
      currency: "EUR",
    });

  return (
    <div className="chip-card">
      <div>
        <div className="chip-title" title={title}>
          {title}
        </div>
        {Number.isFinite(Number(price)) && (
          <div className="chip-sub">{fmt(price)}</div>
        )}
      </div>
      <button
        type="button"
        className="chip-add"
        onClick={onAdd}
        aria-label="Aggiungi"
        title="Aggiungi"
      >
        <span aria-hidden="true">+</span>
      </button>
    </div>
  );
}

function MiniCard({ title, sub, img, onAdd }) {
  if (!title || !String(title).trim()) return null;
  return (
    <div className="mini-card">
      <span className="cm-forgotten-thumb" aria-hidden="true"><Shirt size={26} />
        {img && <img key={img} src={img} alt="" loading="lazy" onError={event => { event.currentTarget.style.visibility = "hidden"; }} />}
      </span>
      <div style={{ minWidth: 0 }}>
        <div className="title" title={title}>
          {title}
        </div>
        {sub ? (
          <div className="sub" title={sub}>
            {sub}
          </div>
        ) : null}
      </div>
      <button
        type="button"
        className="chip-add"
        onClick={onAdd}
        aria-label="Aggiungi"
        title="Aggiungi"
      >
        <span aria-hidden="true">+</span>
      </button>
    </div>
  );
}

/* ---------- CalendarDatePicker ---------- */
function CalendarDatePicker({
  value,
  allowedDates = [],
  onChange,
  title = "Data",
}) {
  const allowedSet = useMemo(() => new Set(allowedDates), [allowedDates]);
  const minAllowed = useMemo(() => {
    if (!allowedDates.length) return null;
    return allowedDates.slice().sort()[0];
  }, [allowedDates]);
  const maxAllowed = useMemo(() => {
    if (!allowedDates.length) return null;
    return allowedDates.slice().sort().slice(-1)[0];
  }, [allowedDates]);

  const initialMonth = value || minAllowed || toValue(new Date());
  const [monthCursor, setMonthCursor] = useState(initialMonth.slice(0, 7));

  useEffect(() => {
    if (value) setMonthCursor(value.slice(0, 7));
  }, [value]);

  const canGoPrev = useMemo(() => {
    if (!minAllowed) return false;
    const [y, m] = monthCursor.split("-").map(Number);
    const cur = new Date(y, (m || 1) - 1, 1);
    const [y2, m2] = minAllowed.split("-").map(Number);
    const min = new Date(y2, (m2 || 1) - 1, 1);
    return cur > min;
  }, [monthCursor, minAllowed]);

  const canGoNext = useMemo(() => {
    if (!maxAllowed) return false;
    const [y, m] = monthCursor.split("-").map(Number);
    const cur = new Date(y, (m || 1) - 1, 1);
    const [y2, m2] = maxAllowed.split("-").map(Number);
    const max = new Date(y2, (m2 || 1) - 1, 1);
    return cur < max;
  }, [monthCursor, maxAllowed]);

  const gotoPrev = () => {
    const [y, m] = monthCursor.split("-").map(Number);
    const d = new Date(y, (m || 1) - 2, 1);
    setMonthCursor(`${d.getFullYear()}-${pad2(d.getMonth() + 1)}`);
  };

  const gotoNext = () => {
    const [y, m] = monthCursor.split("-").map(Number);
    const d = new Date(y, m || 1, 1);
    setMonthCursor(`${d.getFullYear()}-${pad2(d.getMonth() + 1)}`);
  };

  const gridDays = useMemo(() => {
    const [y, m] = monthCursor.split("-").map(Number);
    const firstOfMonth = new Date(y, (m || 1) - 1, 1);
    const startDay = (firstOfMonth.getDay() + 6) % 7;
    const daysInMonth = new Date(y, m || 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push(new Date(y, (m || 1) - 1, d));
    }
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [monthCursor]);

  const todayStr = toValue(new Date());
  const sel = value || "";

  return (
    <div
      className="alc-cal"
      aria-label={title}
      style={{
        width: 276,
        border: "1px solid #e9edf2",
        borderRadius: 12,
        padding: 8,
        background: "#fff",
        boxShadow: "0 8px 20px rgba(0,0,0,0.05)",
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "auto 1fr auto",
          alignItems: "center",
          gap: 8,
          padding: "2px 4px 8px",
        }}
      >
        <button
          type="button"
          onClick={gotoPrev}
          disabled={!canGoPrev}
          aria-label="Mese precedente"
          style={navBtnStyle(!canGoPrev)}
        >
          ‹
        </button>
        <div style={{ textAlign: "center", fontWeight: 700 }}>
          {(() => {
            const [y, m] = monthCursor.split("-").map(Number);
            const dt = new Date(y, (m || 1) - 1, 1);
            return dt.toLocaleDateString("it-IT", {
              month: "long",
              year: "numeric",
            });
          })()}
        </div>
        <button
          type="button"
          onClick={gotoNext}
          disabled={!canGoNext}
          aria-label="Mese successivo"
          style={navBtnStyle(!canGoNext)}
        >
          ›
        </button>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 4,
          padding: "0 2px 4px",
          color: "#6b7280",
          fontSize: 12,
          textAlign: "center",
        }}
      >
        {["L", "M", "M", "G", "V", "S", "D"].map((d) => (
          <div key={d}>{d}</div>
        ))}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 4,
        }}
      >
        {gridDays.map((d, idx) => {
          if (!d) return <div key={idx} />;
          const v = toValue(d);
          const allowed = allowedSet.has(v);
          const isToday = v === todayStr;
          const isSel = v === sel;

          return (
            <button
              key={v}
              type="button"
              disabled={!allowed}
              onClick={() => allowed && onChange?.(v)}
              aria-label={fmtDateLabel(d)}
              className="alc-cal-day"
              style={dayBtnStyle({ allowed, isToday, isSel })}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>

      {!allowedDates.length && (
        <div
          style={{
            color: "#9ca3af",
            fontSize: 12,
            textAlign: "center",
            marginTop: 6,
          }}
        >
          Nessuna data disponibile
        </div>
      )}
    </div>
  );
}

function DatePopover({
  value,
  allowedDates = [],
  onChange,
  label = "Scegli data",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const popupRef = useRef(null);
  const [popupStyle, setPopupStyle] = useState({ visibility: "hidden" });

  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 520px)").matches
      : false,
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(max-width: 520px)");
    const handler = (e) => setIsMobile(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);

  useEffect(() => {
    if (!open || isMobile) return;
    const onDocClick = (e) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target) && !popupRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open, isMobile]);

  useLayoutEffect(() => {
    if (!open || disabled || isMobile) return;
    const reposition = () => {
      if (!ref.current || !popupRef.current) return;
      const trigger = ref.current.getBoundingClientRect();
      const panel = ref.current.closest(".cm-panel")?.getBoundingClientRect();
      const viewport = window.visualViewport;
      const vx = viewport?.offsetLeft || 0;
      const vy = viewport?.offsetTop || 0;
      const vw = viewport?.width || window.innerWidth;
      const vh = viewport?.height || window.innerHeight;
      const leftBound = Math.max(vx, panel?.left ?? vx) + 8;
      const rightBound = Math.min(vx + vw, panel?.right ?? vx + vw) - 8;
      const topBound = Math.max(vy, panel?.top ?? vy) + 8;
      const bottomBound = Math.min(vy + vh, panel?.bottom ?? vy + vh) - 8;
      const width = Math.max(0, Math.min(300, rightBound - leftBound));
      const maxHeight = Math.max(0, bottomBound - topBound);
      const height = Math.min(popupRef.current.scrollHeight, maxHeight);
      const below = trigger.bottom + 6;
      const above = trigger.top - height - 6;
      const top = below + height <= bottomBound ? below : above >= topBound ? above : Math.max(topBound, bottomBound - height);
      const left = Math.min(Math.max(trigger.left, leftBound), rightBound - width);
      setPopupStyle({ left, top, width, maxHeight, visibility: "visible" });
    };
    reposition();
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    window.visualViewport?.addEventListener("resize", reposition);
    window.visualViewport?.addEventListener("scroll", reposition);
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(reposition) : null;
    if (popupRef.current) observer?.observe(popupRef.current);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
      window.visualViewport?.removeEventListener("resize", reposition);
      window.visualViewport?.removeEventListener("scroll", reposition);
      observer?.disconnect();
    };
  }, [open, disabled, isMobile]);

  const pretty = useMemo(() => {
    if (!value) return label;
    const d = fromValue(value);
    return d ? fmtDateLabel(d) : label;
  }, [value, label]);

  const handlePick = (d) => {
    onChange?.(d);
    setOpen(false);
  };

  return (
    <div className="cm-date-trigger" ref={ref}>
      <button
        type="button"
        className="cm-date-btn"
        aria-haspopup="dialog"
        aria-expanded={!disabled && open}
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        title={label}
      >
        <span className="cm-date-ico" aria-hidden>
          📅
        </span>
        <span className="cm-date-text">{pretty}</span>
      </button>

      {!isMobile && open && !disabled && createPortal(
        <div
          className="cal-popover cal-popover-rightalign cm-cart-calendar cm-cart-calendar--desktop"
          ref={popupRef}
          style={popupStyle}
          role="dialog"
          aria-label={label}
        >
          <CalendarDatePicker
            value={value}
            allowedDates={allowedDates}
            onChange={handlePick}
            title={label}
          />
        </div>
      , document.body)}

      {isMobile && open && !disabled && createPortal(
        <div
          className="cm-cal-overlay cm-cart-calendar cm-cart-calendar--mobile"
          role="dialog"
          aria-modal="true"
          aria-label={label}
          onClick={() => setOpen(false)}
        >
          <div className="cm-cal-modal" onClick={(e) => e.stopPropagation()}>
            <CalendarDatePicker
              value={value}
              allowedDates={allowedDates}
              onChange={handlePick}
              title={label}
            />
            <button
              type="button"
              className="cm-cal-close"
              onClick={() => setOpen(false)}
              aria-label="Chiudi"
            >
              Chiudi
            </button>
          </div>
        </div>
      , document.body)}
    </div>
  );
}

const navBtnStyle = (disabled) => ({
  width: 28,
  height: 28,
  borderRadius: 8,
  border: "1px solid #e5e7eb",
  background: "#fff",
  cursor: disabled ? "not-allowed" : "pointer",
  color: disabled ? "#cbd5e1" : "#111827",
  fontWeight: 800,
  lineHeight: "26px",
});

const dayBtnStyle = ({ allowed, isToday, isSel }) => ({
  height: 32,
  borderRadius: 8,
  border: isSel ? "2px solid #00cfd5" : "1px solid #e5e7eb",
  background: isSel ? "#e6f9fa" : allowed ? "#fff" : "#f3f4f6",
  color: allowed ? "#111827" : "#9ca3af",
  cursor: allowed ? "pointer" : "not-allowed",
  fontWeight: isToday ? 800 : 600,
});

function ReferralBanner({ amountCents }) {
  if (!Number.isFinite(Number(amountCents)) || amountCents <= 0) return null;
  const fmt = (cents) =>
    (Number(cents) / 100).toLocaleString("it-IT", {
      style: "currency",
      currency: "EUR",
    });

  return (
    <div className="ref-banner" role="status" aria-live="polite">
      <div className="ref-ico" aria-hidden>
        🎁
      </div>
      <div className="ref-txt">
        <div className="ref-title">Sconto primo ordine</div>
        <div className="ref-sub">
          Grazie a un invito, stai ricevendo <b>{fmt(amountCents)}</b> di sconto
          automatico.
        </div>
      </div>
    </div>
  );
}

export default function CartModal() {
  const {
    items,
    removeItem,
    isOpen,
    closeCart,
    changeQty,
    cartMeta,
    setCartMeta,
    supplierId,
    totals,
    setLogisticsFee,
    addItems,
    promo,
    applyPromo,
    removePromo,
    refreshAutoPromos,
    clearCart,
    autoPromos,
    autoDiscountCents,
  } = useCart();

  const token = localStorage.getItem("token");
  const analyticsCartOpen = useRef(false);
  useEffect(()=>{
    if (isOpen && !analyticsCartOpen.current) track('view_cart',{screen:'cart',items});
    analyticsCartOpen.current=isOpen;
  },[isOpen,items]);

  const [wallet, setWallet] = useState(null);
  const [walletLoading, setWalletLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [isPaying, setIsPaying] = useState(false);
  const [payError, setPayError] = useState("");

  const [serviceLeadTimeMap, setServiceLeadTimeMap] = useState({});
  const [serviceImageMap, setServiceImageMap] = useState({});
  const [fullAddress, setFullAddress] = useState("");
  const [supplierInfo, setSupplierInfo] = useState(null);
  // Indirizzo hub: { status: "idle"|"loading"|"ready"|"error", data, error }
  const [hubSchedule, setHubSchedule] = useState({ status: "idle" });

  const isOrganizationFlow =
    cartMeta?.address_selection?.source_type === "organization";
  const hubAddressId = isOrganizationFlow
    ? Number(cartMeta?.address_selection?.source_id) || null
    : null;

  const navigate = useNavigate();

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && closeCart();
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [isOpen, closeCart]);

  useEffect(() => {
    if (!isOpen) return;
    if (!token) {
      setWallet(null);
      setWalletLoading(false);
      return;
    }

    let cancelled = false;
    setWalletLoading(true);

    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/wallet/me`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: token ? `Bearer ${token}` : "",
          },
          credentials: "include",
        });

        if (cancelled) return;

        if (res.ok) {
          const data = await res.json();
          setWallet(data);
        } else {
          setWallet(null);
        }
      } catch {
        if (!cancelled) setWallet(null);
      } finally {
        if (!cancelled) setWalletLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, token]);

  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const hasAddressSelection =
          cartMeta?.address_selection?.source_type &&
          cartMeta?.address_selection?.source_id != null;

        if (
          hasAddressSelection &&
          cartMeta.address_selection.source_type === "organization"
        ) {
          const rows = await listAddressOptions(token);
          const safeRows = Array.isArray(rows) ? rows : [];
          const chosen =
            safeRows.find(
              (r) =>
                String(r.source_type) ===
                  String(cartMeta.address_selection.source_type) &&
                Number(r.source_id ?? r.id) ===
                  Number(cartMeta.address_selection.source_id),
            ) || null;

          if (chosen) {
            const full =
              [
                chosen.street,
                [chosen.cap, chosen.citta].filter(Boolean).join(" "),
              ]
                .filter(Boolean)
                .join(", ") +
              (chosen.provincia ? ` (${chosen.provincia})` : "");
            setFullAddress(full);
          } else {
            setFullAddress("");
          }
          return;
        }

        const rows = await listAddressOptions(token);
        const safeRows = Array.isArray(rows) ? rows : [];

        const active =
          safeRows.find((r) => r.source_type === "user" && r.active) ||
          safeRows.find((r) => r.active) ||
          safeRows[0];

        if (active && !hasAddressSelection) {
          const display =
            active.display_name ||
            active.label ||
            [
              active.street,
              [active.cap, active.citta].filter(Boolean).join(" "),
            ]
              .filter(Boolean)
              .join(", ") + (active.provincia ? ` (${active.provincia})` : "");

          setCartMeta((m) => ({
            ...m,
            indirizzo: display,
            address_selection: {
              source_type: String(active.source_type || "user"),
              source_id: Number(active.source_id ?? active.id),
              label: active.label || "Indirizzo",
            },
          }));
        }

        const chosen =
          safeRows.find(
            (r) =>
              cartMeta?.address_selection?.source_type &&
              cartMeta?.address_selection?.source_id != null &&
              String(r.source_type) ===
                String(cartMeta.address_selection.source_type) &&
              Number(r.source_id ?? r.id) ===
                Number(cartMeta.address_selection.source_id),
          ) ||
          safeRows.find((r) => r.active) ||
          safeRows[0];

        if (chosen) {
          const full =
            [
              chosen.street,
              [chosen.cap, chosen.citta].filter(Boolean).join(" "),
            ]
              .filter(Boolean)
              .join(", ") + (chosen.provincia ? ` (${chosen.provincia})` : "");
          setFullAddress(full);
        } else {
          setFullAddress("");
        }
      } catch {
        /* noop */
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, token]);

  const [supplierFasce, setSupplierFasce] = useState([]);
  const [chiusure, setChiusure] = useState([]);

  const prevMinDropoffRef = useRef(null);
  const prevDropoffRef = useRef({ date: null, slot: null });
  const prevLeadMinutesRef = useRef(0);

  useEffect(() => {
    if (!hubAddressId) {
      setHubSchedule({ status: "idle" });
      return;
    }
    if (!isOpen) return;

    let cancelled = false;
    // in ricarica teniamo le fasce dello stesso hub: azzerarle cancellerebbe
    // la data/fascia già scelte
    setHubSchedule((prev) =>
      prev.status === "ready" &&
      Number(prev.data?.address_id) === Number(hubAddressId)
        ? prev
        : { status: "loading" },
    );

    (async () => {
      try {
        const data = await getHubAddressSchedule(hubAddressId, token);
        if (!cancelled) setHubSchedule({ status: "ready", data });
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setHubSchedule({
            status: "error",
            error: err?.message || "Errore caricamento fasce hub",
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, hubAddressId, token]);

  const hubData = hubSchedule.status === "ready" ? hubSchedule.data : null;
  const hubSupplierMismatch =
    !!hubData &&
    !!supplierId &&
    Number(hubData.supplier_id) !== Number(supplierId);

  // Con indirizzo hub: solo le fasce dell'hub (mai quelle del fornitore)
  const fasceOrarie = useMemo(() => {
    if (!isOrganizationFlow) return supplierFasce;
    if (!hubData || hubSupplierMismatch) return [];
    return hubSlotsToFasce(hubData.time_slots || []);
  }, [isOrganizationFlow, supplierFasce, hubData, hubSupplierMismatch]);

  const hubBlockReason = !isOrganizationFlow
    ? ""
    : hubSchedule.status === "loading" || hubSchedule.status === "idle"
    ? "Caricamento fasce hub…"
    : hubSchedule.status === "error"
    ? hubSchedule.error
    : hubSupplierMismatch
    ? `Il ritiro presso ${
        hubData.organization_name || "l'organizzazione"
      } è disponibile solo con ${
        hubData.supplier_name || "il fornitore dell'hub"
      }`
    : !(hubData?.time_slots || []).length
    ? "Nessuna fascia configurata per questo hub, contatta l'assistenza"
    : "";

  useEffect(() => {
    if (!isOpen || !supplierId) return;

    (async () => {
      try {
        const data = await getFornitoreCompleto(supplierId, token);

        setSupplierFasce(
          Array.isArray(data?.fasce_orarie) ? data.fasce_orarie : [],
        );
        // Chiusure/ferie valgono anche per l'hub: il fornitore del carrello
        // coincide con quello dell'hub (altrimenti il checkout è bloccato)
        setChiusure(Array.isArray(data?.chiusure) ? data.chiusure : []);

        const f = data?.fornitore ?? data ?? {};
        setSupplierInfo({
          id: supplierId,
          nome: f?.nome_fornitore ?? f?.nome ?? "",
          email: f?.email ?? "",
          telefono: f?.telefono ?? "",
          street: f?.street ?? f?.indirizzo ?? "",
          cap: f?.cap ?? "",
          citta: f?.citta ?? "",
          provincia: f?.provincia ?? "",
          piva: f?.piva ?? f?.partita_iva ?? "",
          cover_url:
            f?.cover_url ??
            data?.cover_url ??
            f?.logo_url ??
            data?.logo_url ??
            "",
        });

        const services = Array.isArray(data?.servizi) ? data.servizi : [];
        const imageMap = {};
        for (const service of services) {
          const sid = service?.id_servizio ?? service?.servizio_id ?? service?.id;
          const image = service?.immagine || service?.image || service?.image_url;
          if (sid != null && typeof image === "string" && /^https?:\/\//i.test(image)) imageMap[sid] = image;
        }
        setServiceImageMap(imageMap);
        const lmap = {};
        for (const s of services) {
          const sid = s?.id_servizio ?? s?.servizio_id ?? s?.id ?? null;
          const min = Number(
            s?.lead_time_min ?? s?.lead_time_minutes ?? s?.lead_minutes ?? 0,
          );
          if (sid != null && Number.isFinite(min) && min > 0) {
            lmap[sid] = min;
          }
        }
        setServiceLeadTimeMap(lmap);

        const feeCandidates = [
          data?.logistics_fee,
          data?.logisticsFee,
          data?.costo_logistica,
          data?.costo_ritiro_consegna,
          data?.delivery_fee,
          data?.tariffa_logistica,
        ].filter((v) => v !== undefined && v !== null);

        if (feeCandidates.length) {
          const feeEUR = Number(String(feeCandidates[0]).replace(",", "."));
          if (!Number.isNaN(feeEUR)) setLogisticsFee(feeEUR, { exempt: data.logistics_fee_exempt === true });
        }
      } catch {
        setSupplierFasce([]);
        setServiceLeadTimeMap({});
        setSupplierInfo(null);
        setChiusure([]);
      }
    })();
  }, [isOpen, supplierId, token, setLogisticsFee]);

  // Hub: la fascia configurata (es. 11:00–11:20) è un unico slot, non va
  // divisa in slot da 30 minuti
  const slotOpts = { wholeWindows: isOrganizationFlow };

  const slotsFor = (dateStr, tipo) =>
    slotsForDate(fasceOrarie, dateStr, tipo, slotOpts);

  const effectiveClosures = chiusure;

  const slotsForAfter = (dateStr, tipo, minDt = null) =>
    slotsForDateAfter(
      fasceOrarie,
      effectiveClosures,
      dateStr,
      tipo,
      minDt,
      slotOpts,
    );

  const pickupSlotsFor = (dateStr) => {
    if (!dateStr) return [];
    if (isDateClosedForType(dateStr, effectiveClosures, "ritiro")) return [];

    const todayStr = toValue(new Date());
    if (dateStr === todayStr) {
      return slotsForAfter(dateStr, "ritiro", new Date());
    }
    return slotsFor(dateStr, "ritiro");
  };

  const getItemLeadMinutes = useCallback(
    (it) => {
      let m = 0;
      if (Number.isFinite(Number(it?.lead_minutes))) {
        m += Number(it.lead_minutes);
      }
      if (Number.isFinite(Number(it?.lead_hours))) {
        m += Number(it.lead_hours) * 60;
      }
      if (m > 0) return m;

      const sid = it?.id_servizio ?? it?.service_id ?? it?.servizio_id ?? null;
      if (sid != null && Number.isFinite(Number(serviceLeadTimeMap[sid]))) {
        return Number(serviceLeadTimeMap[sid]);
      }

      return 0;
    },
    [serviceLeadTimeMap],
  );

  const maxLeadMinutesFromDb = useMemo(() => {
    let max = 0;
    for (const it of items) {
      const m = getItemLeadMinutes(it);
      if (m > max) max = m;
    }
    if (max === 0) return computeLeadMinutes(items);
    return max;
  }, [items, getItemLeadMinutes]);

  const pickupDate = useMemo(
    () => (cartMeta.ritiro_at ? toValue(new Date(cartMeta.ritiro_at)) : ""),
    [cartMeta.ritiro_at],
  );

  const pickupDT = useMemo(
    () => toDateTime(pickupDate, cartMeta.ritiro_slot || ""),
    [pickupDate, cartMeta.ritiro_slot],
  );

  // Hub: nessun tempo di lavorazione, la consegna deve solo seguire il ritiro
  const leadMinutes = isOrganizationFlow ? 0 : maxLeadMinutesFromDb;

  const minDropoffDT = useMemo(() => {
    if (!pickupDT) return null;
    return new Date(pickupDT.getTime() + leadMinutes * 60000);
  }, [pickupDT, leadMinutes]);

  const dropoffDate = useMemo(
    () => (cartMeta.consegna_at ? toValue(new Date(cartMeta.consegna_at)) : ""),
    [cartMeta.consegna_at],
  );

  useEffect(() => {
    prevDropoffRef.current = {
      date: dropoffDate,
      slot: cartMeta.consegna_slot || null,
    };
  }, [dropoffDate, cartMeta.consegna_slot]);

  const hasPickup = useMemo(
    () => Boolean(pickupDate && (cartMeta.ritiro_slot || "").trim()),
    [pickupDate, cartMeta.ritiro_slot],
  );

  const hasDropoff = useMemo(
    () => Boolean(dropoffDate && (cartMeta.consegna_slot || "").trim()),
    [dropoffDate, cartMeta.consegna_slot],
  );

  const canCheckout = hasPickup && hasDropoff && !hubBlockReason;

  const checkoutBlockReason = hubBlockReason
    ? hubBlockReason
    : !hasPickup
    ? "Seleziona data e orario di ritiro"
    : !hasDropoff
    ? "Seleziona data e orario di riconsegna"
    : "";

  const computeAvailableDates = (tipo, horizonDays = 14, minDt = null) =>
    availableDates({
      fasce: fasceOrarie,
      closures: effectiveClosures,
      tipo,
      horizonDays,
      minDt,
      wholeWindows: isOrganizationFlow,
      maxDate: getBookingMaxDate(),
    });

  const pickupAllowedDates = useMemo(
    () => computeAvailableDates("ritiro", 90, new Date()),
    [fasceOrarie, effectiveClosures, isOrganizationFlow],
  );

  const dropoffAllowedDates = useMemo(
    () => computeAvailableDates("consegna", 90, minDropoffDT),
    [fasceOrarie, effectiveClosures, minDropoffDT, isOrganizationFlow],
  );

  const earliestDropoffSuggestion = useMemo(() => {
    if (!minDropoffDT) return null;
    for (const d of dropoffAllowedDates) {
      const slots = slotsForAfter(d, "consegna", minDropoffDT);
      if (slots.length) return { date: d, slot: slots[0] };
    }
    return null;
  }, [dropoffAllowedDates, minDropoffDT]);

  useEffect(() => {
    const isAllowed = (v, arr) => arr.includes(v);

    if (pickupDate && !isAllowed(pickupDate, pickupAllowedDates)) {
      setCartMeta((m) => ({ ...m, ritiro_at: null, ritiro_slot: null }));
    }
    if (dropoffDate && !isAllowed(dropoffDate, dropoffAllowedDates)) {
      setCartMeta((m) => ({ ...m, consegna_at: null, consegna_slot: null }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickupAllowedDates, dropoffAllowedDates]);

  useEffect(() => {
    if (!dropoffDate) {
      prevMinDropoffRef.current = minDropoffDT;
      prevLeadMinutesRef.current = leadMinutes;
      return;
    }

    const allowedSlots = slotsForAfter(dropoffDate, "consegna", minDropoffDT);

    const leadIncreased = leadMinutes > (prevLeadMinutesRef.current || 0);
    const hadPrevSelection = !!(
      prevDropoffRef.current.date && prevDropoffRef.current.slot
    );

    if (!allowedSlots.length) {
      setCartMeta((m) => ({ ...m, consegna_at: null, consegna_slot: null }));
      if (leadIncreased && hadPrevSelection) {
        toast((t) => (
          <span>
            Il nuovo capo richiede più tempo di lavorazione.
            <br />
            La riconsegna selezionata non è più disponibile.
            <br />
            Controlla la nuova data.
            <div style={{ marginTop: 8, textAlign: "right" }}>
              <button
                className="sv-btn sv-btn-primary"
                onClick={() => toast.dismiss(t.id)}
              >
                Ok
              </button>
            </div>
          </span>
        ));
      }
      prevMinDropoffRef.current = minDropoffDT;
      prevLeadMinutesRef.current = leadMinutes;
      return;
    }

    if (!allowedSlots.includes(cartMeta.consegna_slot || "")) {
      const next = allowedSlots[0];
      applyDropoff(dropoffDate, next);

      if (leadIncreased && hadPrevSelection) {
        const d = fromValue(dropoffDate);
        const when = d ? `${fmtDateLabel(d)} ${next}` : next;
        toast.success(
          `Il nuovo capo ha spostato la riconsegna a ${when}. Controlla la nuova data.`,
        );
      }
    }

    prevMinDropoffRef.current = minDropoffDT;
    prevLeadMinutesRef.current = leadMinutes;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropoffDate, minDropoffDT, fasceOrarie, leadMinutes]);

  function applyPickup(dateStr, slotLabel) {
    const d = (dateStr || "").trim();
    const s = (slotLabel || "").trim();
    if (!d || !s) return;
    if (d > getBookingMaxDate()) return;
    const start = s.split("–")[0];
    const iso = new Date(`${d}T${start}:00`).toISOString();
    setCartMeta((m) => ({ ...m, ritiro_at: iso, ritiro_slot: s }));
  }

  function applyDropoff(dateStr, slotLabel) {
    const d = (dateStr || "").trim();
    const s = (slotLabel || "").trim();
    if (!d || !s) return;
    if (d > getBookingMaxDate()) return;
    const start = s.split("–")[0];
    const iso = new Date(`${d}T${start}:00`).toISOString();
    setCartMeta((m) => ({ ...m, consegna_at: iso, consegna_slot: s }));
  }

  useEffect(() => {
    if (pickupDate && !dropoffDate && earliestDropoffSuggestion) {
      applyDropoff(
        earliestDropoffSuggestion.date,
        earliestDropoffSuggestion.slot,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickupDate, dropoffDate, earliestDropoffSuggestion]);

  const referralDiscountCents = useMemo(() => {
    return (autoPromos || [])
      .filter((p) => (p.kind || "").toLowerCase() === "referral")
      .reduce((sum, p) => {
        const eur = Number(p.discount || 0);
        return sum + Math.round(eur * 100);
      }, 0);
  }, [autoPromos]);

  const [suggestions, setSuggestions] = useState([]);
  const [forgotten, setForgotten] = useState([]);

  useEffect(() => {
    if (!isOpen || !supplierId) return;

    (async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const r1 = await fetch(
          `${API_BASE}/api/cart/suggestions?supplierId=${encodeURIComponent(
            supplierId,
          )}`,
          { headers },
        );
        const r2 = await fetch(
          `${API_BASE}/api/cart/forgotten?supplierId=${encodeURIComponent(
            supplierId,
          )}`,
          { headers },
        );

        const js1 = r1.ok ? await r1.json() : [];
        const js2 = r2.ok ? await r2.json() : [];

        const rawSug = Array.isArray(js1)
          ? js1
          : Array.isArray(js1.items)
          ? js1.items
          : Array.isArray(js1.chips?.items)
          ? js1.chips.items
          : [];

        const rawFor = Array.isArray(js2)
          ? js2
          : Array.isArray(js2.items)
          ? js2.items
          : Array.isArray(js2.carousel?.items)
          ? js2.carousel.items
          : [];

        const cleanedSuggestions = filterOutCartDuplicates(rawSug, items);
        const randomSuggestions = shuffleArray(cleanedSuggestions).slice(
          0,
          MAX_SUGGESTIONS_ITEMS,
        );

        setSuggestions(randomSuggestions);
        setForgotten(filterOutCartDuplicates(rawFor, items));
      } catch {
        setSuggestions([]);
        setForgotten([]);
      }
    })();
  }, [isOpen, supplierId, token, items]);

  async function addSuggestedOne(s) {
    if (!supplierId || !s || !s.name) return;

    const cartIdBase = s.serviceId ? `svc-${s.serviceId}` : `sugg-${s.id}`;
    const item = {
      id: `${cartIdBase}-${supplierId}`,
      id_fornitore: supplierId,
      id_servizio: s.serviceId ?? null,
      cross_sell_item_id: s.crossSellItemId,
      descrizione: s.name,
      descrizione_capo: s.name,
      prezzo: Number(s.price || 0),
      qty: 1,
      fornitore_nome: items[0]?.fornitore_nome || "",
    };

    await hapticLight();
    const res = addItems([item], { allowReplace: false });
    track('add_to_cart_attempt',{catalog_id:Number(item.id_servizio)});

    if (!res?.ok && res?.reason === "DIFFERENT_SUPPLIER") {
      if (
        window.confirm(
          "Nel carrello c’è un altro fornitore. Svuotare e aggiungere questo articolo?",
        )
      ) {
        await hapticMedium();
        addItems([item], { allowReplace: true });
      }
    }
  }

  const hasItems = items.length > 0;
  const supplierName = items[0]?.fornitore_nome || "—";
  const subtotalCents = totals.subtotalCents;
  const deliveryCents = totals.feeCents;
  const discountCents = totals.discountCents;
  const totalCents = totals.totalCents;

  const hasSuggestions = Array.isArray(suggestions) && suggestions.length > 0;
  const hasForgotten = Array.isArray(forgotten) && forgotten.length > 0;

  const rawBalance = wallet?.balance_cents;
  const walletBalanceCents = Number.isFinite(Number(rawBalance))
    ? Number(rawBalance)
    : 0;

  const hasEnoughWallet = walletBalanceCents >= totalCents;
  const walletBalanceLabel = (walletBalanceCents / 100).toFixed(2);
  const canUseWallet = !walletLoading && wallet && walletBalanceCents >= 0;

  function buildCheckoutPayload() {
    const pickupSlot = cartMeta.ritiro_slot || null;
    const dropoffSlot = cartMeta.consegna_slot || null;
    const pickupStartHHMM = pickupSlot ? parseSlotStart(pickupSlot) : null;
    const dropoffStartHHMM = dropoffSlot ? parseSlotStart(dropoffSlot) : null;

    const pickupLocal =
      pickupStartHHMM && pickupDate
        ? `${pickupDate}T${pickupStartHHMM}:00`
        : null;
    const dropoffLocal =
      dropoffStartHHMM && dropoffDate
        ? `${dropoffDate}T${dropoffStartHHMM}:00`
        : null;

    const addressSelection =
      cartMeta?.address_selection &&
      cartMeta.address_selection.source_type &&
      cartMeta.address_selection.source_id != null
        ? {
            source_type: String(cartMeta.address_selection.source_type),
            source_id: Number(cartMeta.address_selection.source_id),
            label: cartMeta.address_selection.label || "",
          }
        : null;

    const organizationContext = hubData
      ? {
          organization_id: Number(hubData.organization_id),
          supplier_id: Number(hubData.supplier_id),
        }
      : cartMeta?.organization_context &&
        Number(cartMeta.organization_context.organization_id) > 0
        ? {
            organization_id: Number(
              cartMeta.organization_context.organization_id,
            ),
            supplier_id: Number(
              cartMeta.organization_context.supplier_id || supplierId,
            ),
          }
        : null;

    const resolvedAddress = cartMeta.indirizzo || fullAddress || null;

    const payload = {
      items: items.map((it) => ({
        id_fornitore: it.id_fornitore,
        id_servizio: it.id_servizio ?? null,
        cross_sell_item_id: it.cross_sell_item_id ?? null,
        tipo: it.tipo || null,
        quantita: Math.max(1, Number(it.qty ?? it.quantita ?? 1)),
        prezzo_unit_cents: Math.round(Number(it.prezzo) * 100),
        ritiro_at: cartMeta.ritiro_at || null,
        consegna_at: cartMeta.consegna_at || null,
        indirizzo: resolvedAddress,
        address_selection: addressSelection,
        organization_context: organizationContext,
        fornitore_nome: it.fornitore_nome || null,
        name: `${it.descrizione_capo || it.descrizione || "Servizio"}${
          it.tipo ? ` (${it.tipo})` : ""
        }`,
        lead_minutes: getItemLeadMinutes(it) || null,
        ritiro_slot: pickupSlot,
        consegna_slot: dropoffSlot,
        ritiro_start_hhmm: pickupStartHHMM,
        consegna_start_hhmm: dropoffStartHHMM,
        ritiro_at_local: pickupLocal,
        consegna_at_local: dropoffLocal,
      })),
      currency: "EUR",
      platform: isNativeApp() ? "native" : "web",
      logisticsFee: Number(totals.fee || totals.feeCents / 100 || 0),
      promoCode: promo?.code || null,
      autoPromos,
      auto_discount_eur: autoDiscountCents / 100,

      booking: {
        ritiro_at: cartMeta.ritiro_at || null,
        ritiro_slot: pickupSlot || "",
        consegna_at: cartMeta.consegna_at || null,
        consegna_slot: dropoffSlot || "",
        indirizzo: resolvedAddress || "",
        address_selection: addressSelection,
        organization_context: organizationContext,
      },

      supplier_info: supplierInfo || {
        id: supplierId,
        nome: supplierName,
      },

      ritiro_slot: pickupSlot,
      consegna_slot: dropoffSlot,
      ritiro_start_hhmm: pickupStartHHMM,
      consegna_start_hhmm: dropoffStartHHMM,
      ritiro_at_local: pickupLocal,
      consegna_at_local: dropoffLocal,

      supplierId,
    };

    return payload;
  }

  const canPayNow =
    canCheckout &&
    !isPaying &&
    (paymentMethod === "card" ||
      paymentMethod === "paypal" ||
      (paymentMethod === "wallet" && hasEnoughWallet));

  async function handleCheckoutClick() {
    if (isPaying) return;
    if ((pickupDate && pickupDate > getBookingMaxDate()) || (dropoffDate && dropoffDate > getBookingMaxDate())) {
      track('checkout_blocked',{error_code:'date_limit'});
      toast.error("Puoi prenotare entro due mesi da oggi.", { id: "cart-date-feedback", duration: 3500 });
      return;
    }
    if (!canPayNow) {
      track('checkout_blocked',{error_code:paymentMethod==='wallet'&&!hasEnoughWallet?'insufficient_balance':'checkout_constraint'});
      await hapticError();
      if (!canCheckout && checkoutBlockReason) {
        toast.error(checkoutBlockReason, { id: "cart-date-feedback", duration: 3500 });
      } else if (paymentMethod === "wallet" && !hasEnoughWallet) {
        toast.error("Saldo carta prepagata insufficiente.");
      }
      return;
    }

    setPayError("");
    track('begin_checkout',{screen:'checkout',gateway:paymentMethod==='card'?'stripe':paymentMethod,currency:'EUR',value:totalCents/100,items});

    try {
      setIsPaying(true);
      await hapticMedium();
      const payload = buildCheckoutPayload();

      if (paymentMethod === "wallet") {
        if (!wallet) {
          throw new Error(
            "Impossibile leggere il saldo della carta prepagata.",
          );
        }

        const res = await fetch(`${API_BASE}/api/wallet/pay`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token ? `Bearer ${token}` : "",
          },
          body: JSON.stringify(payload),
        });
        const data = await res.json().catch(() => ({}));

        if (!res.ok || !data.ok) {
          if (data.code === "AUTO_PROMO_CHANGED") refreshAutoPromos();
          throw new Error(
            data.error || "Errore durante il pagamento con carta prepagata",
          );
        }

        track('payment_initiation_result',{gateway:'wallet',outcome:'success'});
        void confirmPurchase(data.session_id);
        clearCart();
        closeCart();
        await hapticSuccess();
        navigate("/checkout/success?wallet=1", { replace: true });
        return;
      }

      if (paymentMethod === "paypal") {
        const res = await fetch(`${API_BASE}/api/paypal/create-order`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token ? `Bearer ${token}` : "",
          },
          body: JSON.stringify(payload),
        });

        const data = await res.json().catch(() => ({}));

        if (!res.ok || !data || !data.approvalUrl) {
          if (data?.code === "AUTO_PROMO_CHANGED") refreshAutoPromos();
          throw new Error(
            data.error || "Errore nell’avvio del pagamento PayPal",
          );
        }
        await hapticSuccess();
        await openExternalUrl(data.approvalUrl);
        track('payment_initiation_result',{gateway:'paypal',outcome:'success'});
        return;
      }

      const res = await fetch(`${API_BASE}/api/checkout/session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));

      if (data?.url) {
        track('payment_initiation_result',{gateway:'stripe',outcome:'success'});
        await hapticSuccess();
        await openExternalUrl(data.url);
      } else {
        throw new Error(data.error || "Errore nell’avvio del pagamento");
      }
    } catch (e) {
      console.error(e);
      const msg = e?.message || "Errore di rete durante il checkout";
      track('payment_initiation_result',{gateway:paymentMethod==='card'?'stripe':paymentMethod,outcome:'failure',error_code:'unavailable'});
      setPayError(msg);
      await hapticError();
      toast.error(msg);
    } finally {
      setIsPaying(false);
    }
  }

  if (!isOpen) return null;

  return (
    <div
      className="cm-overlay"
      onClick={closeCart}
      role="dialog"
      aria-modal="true"
      aria-label="Carrello"
    >
      <div className="cm-panel cm-compact cm-restyled" onClick={(e) => e.stopPropagation()}>
        <header className="op-header compact">
          <div className="row">
            <button
              type="button"
              className="back"
              aria-label="Chiudi"
              onClick={closeCart}
            >
              <span aria-hidden>×</span>
            </button>
            <div className="title">Il tuo ordine</div>
            <div style={{ width: 36, height: 36 }} />
          </div>
        </header>

        <main className="op-main compact">
          <div className="cm-cart-column">
          <div className="cm-items-card">
          {!hasItems && <div className="hint">Il carrello è vuoto</div>}

          {items.map((it) => (
            <LineItem
              key={it.id}
              item={it}
              imageUrl={serviceImageMap[it.id_servizio]}
              onQty={(id, q) => {
                if (q <= 0) removeItem(id);
                else changeQty(id, q);
              }}
              onRemove={(id) => removeItem(id)}
            />
          ))}

          </div>
          {hasItems && hasSuggestions && (
            <details className="suggest-wrap cm-addon">
              <summary><span className="cm-addon-plus"><Plus size={22} aria-hidden="true" /></span>Aggiungi altri servizi<ChevronDown size={20} aria-hidden="true" /></summary>
              <div className="cm-addon-content">
              {suggestions.map((s) => (
                <ChipCard
                  key={s.id ?? `${s.serviceId ?? "x"}-${s.name}`}
                  title={s.name}
                  price={s.price}
                  onAdd={() => addSuggestedOne(s)}
                />
              ))}
              </div>
            </details>
          )}

          {hasItems && hasForgotten && (
            <details className="cm-addon">
              <summary><span className="cm-addon-plus"><Plus size={22} aria-hidden="true" /></span>Hai dimenticato qualcosa?<ChevronDown size={20} aria-hidden="true" /></summary>
              <div className="carousel">
                {forgotten.map((f) => (
                  <MiniCard
                    key={f.id ?? `${f.serviceId ?? "x"}-${f.name}`}
                    title={f.name}
                    img={f.img}
                    sub={f.sub || ""}
                    onAdd={() => addSuggestedOne(f)}
                  />
                ))}
              </div>
            </details>
          )}

          {hasItems && referralDiscountCents > 0 && (
            <ReferralBanner amountCents={referralDiscountCents} />
          )}

          </div>
          {hasItems && (
            <section className="summary compact" aria-label="Riepilogo costi">
              <div className="sum-row">
                <div className="left">Subtotale</div>
                <div>{currency(subtotalCents)}</div>
              </div>

              <div className="sum-row">
                <div className="left">Costo di consegna</div>
                <div>{currency(deliveryCents)}</div>
              </div>

              <div className="sum-row">
                <div className="left">Promo automatiche</div>
                <div style={{ display: "grid", gap: 6, justifyItems: "end" }}>
                  {autoPromos && autoPromos.length > 0 ? (
                    <>
                      <div
                        className="promo-auto-tags"
                        style={{
                          display: "flex",
                          gap: 6,
                          flexWrap: "wrap",
                          justifyContent: "flex-end",
                        }}
                      >
                        {autoPromos.map((p, i) => (
                          <span
                            key={p.id || i}
                            className={`promo-chip ${
                              String(p.kind).toLowerCase() === "referral"
                                ? "promo-chip--ref"
                                : ""
                            }`}
                            title={p.label || p.name || p.kind}
                          >
                            {p.label || p.name || p.kind}
                          </span>
                        ))}
                      </div>
                      {autoDiscountCents > 0 && (
                        <div className="promo-discount">
                          − {currency(autoDiscountCents)}
                        </div>
                      )}
                    </>
                  ) : (
                    <span>—</span>
                  )}
                </div>
              </div>

              <div className="sum-row sum-row--promo">
                <div className="left">Codice promo</div>
                <div style={{ display: "grid", gap: 6, justifyItems: "end" }}>
                  <PromoControls
                    promo={promo}
                    discountCents={discountCents}
                    applyPromo={applyPromo}
                    removePromo={removePromo}
                  />
                </div>
              </div>

              <div className="sum-row total">
                <div className="left">Totale</div>
                <div>{currency(totalCents)}</div>
              </div>

              <div className="sum-hr-after-total" aria-hidden="true" />

              <div className="extra-recap">
                <div className="extra-row">
                  <div className="extra-label"><CalendarDays size={20} aria-hidden="true" />Ritiro:</div>
                  <div
                    className="extra-value"
                    style={{
                      display: "grid",
                      gridTemplateColumns: "auto auto",
                      gap: 6,
                      alignItems: "center",
                    }}
                  >
                    <DatePopover
                      label="Data ritiro"
                      value={pickupDate}
                      allowedDates={pickupAllowedDates}
                      onChange={(d) => {
                        const slots = pickupSlotsFor(d);
                        const first = slots[0] || "";
                        if (d && first) {
                          applyPickup(d, first);
                        } else {
                          setCartMeta((m) => ({
                            ...m,
                            ritiro_at: null,
                            ritiro_slot: null,
                          }));
                        }
                      }}
                    />
                    <select
                      className="slot-select inline"
                      value={cartMeta.ritiro_slot || ""}
                      onChange={(e) => applyPickup(pickupDate, e.target.value)}
                      disabled={
                        !pickupDate || pickupSlotsFor(pickupDate).length === 0
                      }
                    >
                      <option value="" disabled>
                        Orario
                      </option>
                      {pickupSlotsFor(pickupDate).map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="extra-row">
                  <div className="extra-label"><CalendarDays size={20} aria-hidden="true" />Riconsegna:</div>
                  <div
                    className="extra-value"
                    style={{
                      display: "grid",
                      gridTemplateColumns: "auto auto",
                      gap: 6,
                      alignItems: "center",
                    }}
                  >
                    <DatePopover
                      label="Data consegna"
                      value={dropoffDate}
                      disabled={!pickupDate}
                      allowedDates={pickupDate ? dropoffAllowedDates : []}
                      onChange={(d) => {
                        const slots = slotsForAfter(
                          d,
                          "consegna",
                          minDropoffDT,
                        );
                        const first = slots[0] || "";
                        if (d && first) {
                          applyDropoff(d, first);
                        } else {
                          setCartMeta((m) => ({
                            ...m,
                            consegna_at: null,
                            consegna_slot: null,
                          }));
                        }
                      }}
                    />

                    <select
                      className="slot-select inline"
                      value={cartMeta.consegna_slot || ""}
                      onChange={(e) =>
                        applyDropoff(dropoffDate, e.target.value)
                      }
                      disabled={
                        !dropoffDate ||
                        slotsForAfter(dropoffDate, "consegna", minDropoffDT)
                          .length === 0
                      }
                    >
                      <option value="" disabled>
                        Orario
                      </option>
                      {slotsForAfter(dropoffDate, "consegna", minDropoffDT).map(
                        (s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ),
                      )}
                    </select>
                  </div>
                </div>

                <div className="extra-row">
                  <div className="extra-label"><Truck size={20} aria-hidden="true" />Servizio eseguito da:</div>
                  <div className="extra-value">{supplierName}</div>
                </div>

                <div className="extra-row">
                  <div className="extra-label">
                    <MapPin size={20} aria-hidden="true" />Indirizzo di ritiro e consegna:
                  </div>
                  <div className="extra-value">{fullAddress || "—"}</div>
                </div>
              </div>

              {isOrganizationFlow &&
                hubBlockReason &&
                hubSchedule.status !== "loading" && (
                  <div
                    role="alert"
                    style={{
                      marginTop: 10,
                      fontSize: 12,
                      color: "#9f1239",
                      background: "#fff1f2",
                      border: "1px solid #fecdd3",
                      borderRadius: 10,
                      padding: "8px 10px",
                    }}
                  >
                    {hubBlockReason}
                  </div>
                )}

              {chiusure.length > 0 && (
                <div
                  style={{
                    marginTop: 10,
                    fontSize: 12,
                    color: "#8a4b08",
                    background: "#fff7ed",
                    border: "1px solid #fed7aa",
                    borderRadius: 10,
                    padding: "8px 10px",
                  }}
                >
                  Le date disponibili tengono conto delle chiusure configurate
                  dal fornitore.
                </div>
              )}
            </section>
          )}
        </main>

        {hasItems && (
          <div className="sticky-cta compact">
            <fieldset className="payment-methods">
              <legend>Metodo di pagamento</legend>

              <div className="cm-payment-grid">
              <label className={`payment-option ${paymentMethod === "card" ? "payment-option--selected" : ""}`}>
                <input
                  type="radio"
                  name="paymentMethod"
                  value="card"
                  aria-label="Carta di credito / Stripe"
                  checked={paymentMethod === "card"}
                  onChange={() => setPaymentMethod("card")}
                  disabled={isPaying}
                />
                <span className="cm-payment-icon" aria-hidden="true"><CreditCard size={26} /></span>
                <strong aria-hidden="true">Carta</strong>
                <span className="cm-payment-description" aria-hidden="true">Stripe</span>
              </label>

              <label
                className={`payment-option ${paymentMethod === "wallet" ? "payment-option--selected" : ""} ${
                  !canUseWallet ? "payment-option--disabled" : ""
                }`}
              >
                <input
                  type="radio"
                  name="paymentMethod"
                  value="wallet"
                  checked={paymentMethod === "wallet"}
                  onChange={() => setPaymentMethod("wallet")}
                  disabled={!canUseWallet || isPaying}
                />
                <span className="cm-payment-icon" aria-hidden="true"><Wallet size={26} /></span>
                <strong aria-hidden="true">Wallet</strong>
                <span className="cm-payment-description">
                  <span className="cm-wallet-caption">Carta prepagata</span>{" "}
                  <span className="wallet-balance">
                    (saldo: € {walletBalanceLabel}
                    {walletLoading ? " – caricamento…" : ""})
                  </span>
                </span>
              </label>

              <label className={`payment-option ${paymentMethod === "paypal" ? "payment-option--selected" : ""}`}>
                <input
                  type="radio"
                  name="paymentMethod"
                  value="paypal"
                  aria-label="PayPal"
                  checked={paymentMethod === "paypal"}
                  onChange={() => setPaymentMethod("paypal")}
                  disabled={isPaying}
                />
                <span className="cm-payment-icon cm-paypal-icon" aria-hidden="true">P</span>
                <span className="cm-payment-description cm-payment-paypal-label">PayPal</span>
              </label>

              </div>
              {paymentMethod === "wallet" &&
                !walletLoading &&
                canUseWallet &&
                !hasEnoughWallet && (
                  <div className="wallet-warning">
                    Saldo insufficiente per questo ordine.
                    <br />
                    Puoi ricaricare dal profilo.
                  </div>
                )}
            </fieldset>

            <div className="cta-actions">
              <button
                type="button"
                className="btn-clear-cart"
                onClick={async () => {
                  if (window.confirm("Svuotare il carrello?")) {
                    await hapticWarning();
                    clearCart();
                    closeCart();
                  }
                }}
                aria-label="Svuota carrello"
                title="Svuota carrello"
                disabled={isPaying}
              >
                <Trash2 size={24} aria-hidden="true" />
              </button>

              <button
                type="button"
                className="btn-cta"
                aria-label={isPaying ? "Elaborazione…" : "Vai al pagamento"}
                disabled={isPaying || (canCheckout && !canPayNow)}
                aria-disabled={!canPayNow}
                title={
                  !canCheckout
                    ? checkoutBlockReason
                    : paymentMethod === "wallet" && !hasEnoughWallet
                    ? "Saldo carta prepagata insufficiente"
                    : isPaying
                    ? "Pagamento in corso…"
                    : "Procedi al pagamento"
                }
                onClick={handleCheckoutClick}
              >
                {isPaying ? <LoaderCircle size={24} aria-hidden="true" /> : (
                  <svg width="26" height="26" viewBox="0 0 32 32" fill="none" aria-hidden="true">
                    <path d="M18 25H5a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3h21a3 3 0 0 1 3 3v7M2 10h27M7 19h6m3 0h2" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                    <circle cx="24" cy="24" r="8" fill="currentColor" />
                    <path d="m20 24 3 3 5-6" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
            </div>

            {hubBlockReason && (
              <div className="checkout-hint" role="status" aria-live="polite">
                {hubBlockReason}
              </div>
            )}
            {payError && (
              <div className="checkout-error" role="status" aria-live="polite">
                {payError}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
